import "../drawer/DocumentsDrawer.scss";
import "react-tooltip/dist/react-tooltip.css";

import Table from "components/Table/Table";
import { PAGE_SIZE } from "constants/TableConstants";
import { useMemo } from "react";
import { Tooltip } from "react-tooltip";
import type { DocumentTableProps } from "../DocumentTableTypes";
import { useDocumentTableLogic } from "../hooks/useDocumentTableLogic";
import {
  getSelectableMetadocDocumentIds,
  sanitizeMetadocSelectedRows,
} from "../utils/metadocSelection";
import {
  isParseablePayHistoryDocument,
  useDefaultColumns,
} from "./documentColumns";

/**
 * DocumentTable - Table for source documents
 *
 * Uses server-side pagination/sorting.
 */
const DocumentTable = ({
  documents,
  onDelete,
  totalCount = 0,
  page = 1,
  onPageChange,
  isLoading = false,
  isDrawer = false,
  sortColumns = [],
  onSortChange,
  fileServices = [],
  fileId,
  metaDoc = false,
  selectable = false,
  selectedDocumentIds = new Set(),
  onSelectionChange,
  paginationLeftContent,
  onExtractPayHistory,
}: DocumentTableProps) => {
  const selectableMetadocDocumentIds = useMemo(
    () => getSelectableMetadocDocumentIds(documents),
    [documents],
  );
  const isAllEligibleMetadocSelected = useMemo(() => {
    if (selectableMetadocDocumentIds.size === 0) {
      return false;
    }

    return Array.from(selectableMetadocDocumentIds).every((id) =>
      selectedDocumentIds.has(id),
    );
  }, [selectableMetadocDocumentIds, selectedDocumentIds]);
  const hasArScreeningDocuments = documents.some(
    (doc) => doc.service_type === "ars" || doc.amended_review_screening_id,
  );
  const hasParseableDocuments =
    !!onExtractPayHistory && documents.some(isParseablePayHistoryDocument);

  const {
    rows,
    setRows,
    selectedRows,
    updatingDocIds,
    datePickerRefs,
    handleSelectedRowsChange,
    handleToggleCMS,
    handleChangeDocumentType,
    handleChangeDocumentDate,
    handleChangeDocumentService,
    handleCellClick,
  } = useDocumentTableLogic({
    documents,
    fileServices,
    metaDoc,
    selectedDocumentIds,
    onSelectionChange,
  });

  const handleToggleSelectAllEligibleMetadoc = (checked: boolean) => {
    if (!metaDoc) {
      return;
    }

    if (checked) {
      handleSelectedRowsChange(new Set(selectableMetadocDocumentIds));
      return;
    }

    const nextSelection = new Set(selectedDocumentIds);
    selectableMetadocDocumentIds.forEach((id) => nextSelection.delete(id));
    onSelectionChange?.(nextSelection);
  };

  const columns = useDefaultColumns({
    hasArScreeningDocuments,
    hasParseableDocuments,
    metaDoc,
    selectable,
    onDelete,
    handleToggleCMS,
    handleChangeDocumentType,
    handleChangeDocumentDate,
    handleChangeDocumentService,
    updatingDocIds,
    fileServices,
    datePickerRefs,
    setRows,
    fileId,
    isAllEligibleMetadocSelected,
    hasEligibleMetadocDocuments: selectableMetadocDocumentIds.size > 0,
    onToggleSelectAllEligibleMetadoc: handleToggleSelectAllEligibleMetadoc,
    onExtractPayHistory,
  });

  return (
    <>
      <Table
        className="documents-table"
        drawer={isDrawer}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
        rows={rows}
        rowKeyGetter={(row) => row.id}
        onCellClick={handleCellClick}
        isLoading={isLoading}
        pagination={
          onPageChange
            ? {
                pageSize: PAGE_SIZE,
                pageIndex: page,
                rowCount: totalCount,
                onPageChange,
                isLoading,
                leftContent: paginationLeftContent,
              }
            : undefined
        }
        useServerSorting={true}
        serverSorting={{
          sortColumns,
          onSortChange,
        }}
        zebraStripes={true}
        customScrollbars={true}
        selectedRows={metaDoc || selectable ? selectedRows : undefined}
        onSelectedRowsChange={
          metaDoc || selectable ? handleSelectedRowsChange : undefined
        }
        sanitizeSelectedRows={
          metaDoc
            ? (nextSelectedRows, currentSelectedRows) =>
                sanitizeMetadocSelectedRows({
                  nextSelectedRows,
                  currentSelectedRows,
                  documents,
                  metaDoc,
                })
            : undefined
        }
      />
      {/* table positioning interferes with tooltips, so we add it here with an id */}
      <Tooltip
        id="preview-tooltip-main"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip id="parse-tooltip-main" className="custom-tooltip" place="top" />
      <Tooltip
        id="download-tooltip-main"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="delete-tooltip-main"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="document-name-tooltip"
        className="custom-tooltip"
        place="top"
      />
      <Tooltip
        id="original-filename-tooltip"
        className="custom-tooltip"
        place="top"
      />
    </>
  );
};

export default DocumentTable;
